/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {GitObjectID, ID} from './github/types';

import {
  forceLoadDiffPathAtom,
  gitHubPullRequestComparableVersionsAtom,
  gitHubPullRequestJumpToCommentIDAtom,
  gitHubPullRequestSelectedVersionIndexAtom,
  gitHubPullRequestVersionIndexForCommitAtom,
} from './jotai';
import {Box, Link, Text} from '@primer/react';
import {useAtomValue, useSetAtom, useStore} from 'jotai';
import React, {useCallback} from 'react';

type Props = {
  commentID: ID;
  commit: GitObjectID;
  path: string;
};

// eslint-disable-next-line prefer-arrow-callback
export default React.memo(function PullRequestReviewCommentPathLink({
  commentID,
  commit,
  path,
}: Props): React.ReactElement {
  const versionIndex = useAtomValue(gitHubPullRequestVersionIndexForCommitAtom(commit));
  const setJumpToCommentID = useSetAtom(gitHubPullRequestJumpToCommentIDAtom(commentID));
  const setSelectedVersionIndex = useSetAtom(gitHubPullRequestSelectedVersionIndexAtom);
  const setComparableVersions = useSetAtom(gitHubPullRequestComparableVersionsAtom);
  const setForceLoadDiffPath = useSetAtom(forceLoadDiffPathAtom);
  const store = useStore();

  const onClick = useCallback(() => {
    if (versionIndex != null) {
      setForceLoadDiffPath(path);
      setJumpToCommentID(true);

      const currentVersionIndex = store.get(gitHubPullRequestSelectedVersionIndexAtom);
      if (currentVersionIndex !== versionIndex) {
        setSelectedVersionIndex(versionIndex);
      }
      const currentComparable = store.get(gitHubPullRequestComparableVersionsAtom);
      if (
        currentComparable?.beforeCommitID !== null ||
        currentComparable?.afterCommitID !== commit
      ) {
        setComparableVersions({
          beforeCommitID: null,
          afterCommitID: commit,
        });
      }
    }
  }, [commit, path, store, setComparableVersions, setForceLoadDiffPath, setJumpToCommentID, setSelectedVersionIndex, versionIndex]);

  return (
    <Box overflow="hidden" sx={{textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
      <Link as="button" onClick={onClick}>
        <Text fontSize={12}>{path}</Text>
      </Link>
    </Box>
  );
});
